"use client"

import { useStoreAudio } from "@/app/store/slices/audio/hook"
import { useEffect, useMemo, useRef } from "react"
import { Button } from "@heroui/button"
import { MdOutlinePause, MdOutlinePlayArrow } from "react-icons/md"
import { ContentNullWhere } from "./content_null"
import { motion } from "framer-motion"
import { parseTime } from "@internationalized/date"
import { useStoreLive } from "@/app/store/slices/live/hook"
import { PiHandGrabbing, PiHandGrabbingFill, PiMinusBold } from "react-icons/pi"
import { DndContext, DragEndEvent } from "@dnd-kit/core"
import { Draggable } from "./dnd"
import { restrictToParentElement } from "@dnd-kit/modifiers"
import { VideoCardFloat } from "./video"
import { Card, CardBody } from "@heroui/card"
import { Image } from "@heroui/image"
import { PiBroadcastFill } from "react-icons/pi"
import clsx from "clsx"

export function time(second: number) {
  return second >= 3600 ?
    parseTime("00:00").add({ seconds: Math.round(second) }).toString() :
    parseTime("00:00").add({ seconds: Math.round(second) }).toString().substring(3)
}

const url = process.env.NEXT_PUBLIC_ICECAST || "http://localhost:8000/"

export function Audio() {
  const { setIsPlaying, setProgress, isPlaying, setIsLoading, ref: audioRef } = useStoreAudio()
  const { audio: { audio: status, flux } } = useStoreLive(true)

  const path = useMemo(() => {
    return (status && flux?.key) ? (url + flux.key) : null
  }, [status, flux?.key])

  useEffect(() => {
    const audio = audioRef?.current
    if (!audio || !path) return

    if (isPlaying) {
      const playPromise = audio.play()
      if (playPromise !== undefined) {
        playPromise.catch(error => {
          console.warn("Autoplay bloqué ou erreur de chargement :", error)
          setIsPlaying(false)
        })
      }
    } else {
      audio.pause()
    }
  }, [isPlaying, path])

  const handleWaiting = () => setIsLoading(true)
  const handleCanPlay = () => setIsLoading(false)
  
  const handlePlaying = () => {
    setIsLoading(false)
    setIsPlaying(true)
  }

  const handlePause = () => {
    setIsPlaying(false)
  }

  const handleTimeUpdate = (e: React.SyntheticEvent<HTMLAudioElement>) => {
    const target = e.target as HTMLAudioElement
    setProgress(Math.round(target.currentTime))
  }

  const handleError = () => {
    setIsLoading(false)
    setIsPlaying(false)
  }

  if (!path) return null

  return (
    <audio
      hidden
      key={path}
      ref={audioRef}
      src={path}
      crossOrigin="anonymous"
      preload="none"
      onWaiting={handleWaiting}
      onCanPlay={handleCanPlay}
      onPlaying={handlePlaying}
      onPause={handlePause}
      onTimeUpdate={handleTimeUpdate}
      onError={handleError}
      onStalled={handleError}
    />
  )
}

export function LiveContainer() {
  const { audio, video } = useStoreLive()
  const handleDragEnd = (event: DragEndEvent) => {
    if (event.active.id === "audio_main_draggable") {
      audio.setPos(
        audio.pos.x + event.delta.x,
        audio.pos.y + event.delta.y
      )
      audio.toggle("drag")
    }
    if (event.active.id === "video_main_draggable") {
      video.setPos(
        video.pos.x + event.delta.x,
        audio.pos.y + event.delta.y
      )
    }
  }

  return (
    <ContentNullWhere
      where={["/auth/signin", "/auth/pass", "/auth/profile", "/live"]}
    >
      <DndContext id="main-context"
        modifiers={[restrictToParentElement]}
        onDragEnd={handleDragEnd}
      >
        <div className="fixed z-50 pointer-events-none inset-0 bg-transparent p-3 pl-[100px]">
          <div className="relative min-h-full min-w-full max-w-min max-h-min overflow-hidden bg-transparent flex">
            <div className="bg-transparent flex-grow min-w-full min-h-full relative">
              <Draggable id={"audio_main_draggable"} pos={audio.pos}
                disabled={!audio.drag}
              >
                <AudioCardFloat />
              </Draggable>
              <Draggable id={"video_main_draggable"} pos={video.pos} disabled={!video.drag}>
                <VideoCardFloat />
              </Draggable>
            </div>
          </div>
        </div>
      </DndContext>
    </ContentNullWhere>
  )
}

const PulsingDot = () => (
  <span className="relative flex h-2 w-2 mr-1.5">
    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#E31D2B] opacity-75"></span>
    <span className="relative inline-flex rounded-full h-2 w-2 bg-[#E31D2B]"></span>
  </span>
);

export function AudioCardFloat() {
  const { audio: { metadata, audio, expand, toggle, drag } } = useStoreLive(true)
  const { isPlaying, progress, ref: audioRef, isLoading, setIsLoading } = useStoreAudio()

  const handlePlayPause = () => {
    const audio = audioRef?.current
    if (!audio) return

    if (isPlaying) {
      audio.pause()
    } else {
      const playPromise = audio.play()
      if (playPromise !== undefined) {
        playPromise.catch((error) => {
          console.error("Erreur lecture:", error)
          setIsLoading(false)
        })
      }
    }
  }

  if (!expand) return null

  return (
    <motion.div
      layoutId="audio_player"
      initial={{ y: 50, opacity: 0, scale: 0.9 }}
      animate={{ y: 0, opacity: 1, scale: 1 }}
      exit={{ y: 50, opacity: 0, scale: 0.9 }}
      transition={{ type: "spring", stiffness: 300, damping: 30 }}
      className={clsx("z-[60] pointer-events-auto", {
        "cursor-grab": drag
      })}
    >
      <Card
        isBlurred
        className="w-[340px] border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-950/90 backdrop-blur-2xl shadow-2xl rounded-2xl overflow-hidden"
      >
        <div
          className="flex items-center justify-between px-4 py-2 bg-slate-50/50 dark:bg-slate-900/50 border-b border-slate-100 dark:border-slate-800"
        >
          <div className="flex items-center">
            <div className="flex items-center px-2 py-0.5 rounded-full bg-[#E31D2B]/10 border border-[#E31D2B]/10" hidden={!audio}>
              <PulsingDot />
              <span className="text-[9px] font-black text-[#E31D2B] uppercase tracking-widest">En Direct</span>
            </div>
          </div>

          <div className="flex gap-1">
            <Button
              isIconOnly size="sm" variant="light" radius="full"
              className="text-slate-400 hover:text-slate-900 dark:hover:text-white h-7 w-7 min-w-7"
              onPress={() => toggle("expand")}
            >
              <PiMinusBold size={14} />
            </Button>
            <Button
              isIconOnly size="sm" variant="light" radius="full"
              className="text-slate-400 hover:text-slate-900 dark:hover:text-white h-7 w-7 min-w-7"
              onPress={() => toggle("drag")}
            >
              {
                drag ?
                  <PiHandGrabbingFill /> :
                  <PiHandGrabbing size={14} />
              }
            </Button>
          </div>
        </div>

        <CardBody className="flex flex-row gap-4 p-4 items-center">
          <div className="relative h-16 w-16 flex-shrink-0 group">
            <Image
              alt="Sava Live"
              className="object-cover rounded-xl shadow-md border border-slate-100 dark:border-slate-800"
              height={64}
              width={64}
              src="/background-image.jpg"
              fallbackSrc="https://via.placeholder.com/64"
            />
            <div className="absolute inset-0 flex items-center justify-center bg-[#E31D2B]/20 rounded-xl z-10 pointer-events-none">
              <PiBroadcastFill className="text-white drop-shadow-md animate-pulse" size={20} />
            </div>
          </div>

          <div className="flex flex-col flex-grow min-w-0 gap-1">
            <div className="flex flex-col">
              <h3 className="text-sm font-black text-slate-900 dark:text-white leading-tight tracking-tight">
                SAVA <span className="text-[#E31D2B]">RADIO</span>
              </h3>
              <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate font-medium">
                {metadata || "Sakaizan'ny Vahoaka"}
              </p>
            </div>

            <div className="flex items-center justify-between mt-1">
              <Button
                isIconOnly
                radius="full"
                size="md"
                className="bg-[#E31D2B] text-white shadow-lg shadow-red-500/30 hover:scale-105 transition-transform"
                isLoading={isLoading}
                onPress={handlePlayPause}
                isDisabled={!audioRef || !audio}
              >
                {isPlaying ? <MdOutlinePause size={22} /> : <MdOutlinePlayArrow size={24} className="ml-0.5" />}
              </Button>

              <div className="flex flex-col items-end gap-1">
                <div className="h-4 flex items-center">
                  {isPlaying ? <MiniVisualizer /> : <div className="h-[1px] w-8 bg-slate-200 dark:bg-slate-800" />}
                </div>

                <span className="text-[10px] font-bold text-slate-400 tabular-nums">
                  {time(progress)}
                </span>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>
    </motion.div>
  )
}

function MiniVisualizer() {
  return (
    <div className="flex items-end gap-[2px] h-3">
      {[1, 2, 3, 4, 5].map((i) => (
        <motion.div
          key={i}
          className="w-[3px] bg-[#E31D2B] rounded-t-[1px]"
          animate={{ height: ["20%", "100%", "40%", "80%", "20%"] }}
          transition={{
            duration: 0.5 + Math.random() * 0.3,
            repeat: Infinity,
            repeatType: "mirror",
            ease: "easeInOut",
            delay: i * 0.1
          }}
        />
      ))}
    </div>
  )
}